import { Box, ClickAwayListener, FormControl, InputLabel, OutlinedInput, Paper, Popper, useTheme } from '@mui/material'
import { RichTreeView } from '@mui/x-tree-view'
import { useTreeViewApiRef } from '@mui/x-tree-view/hooks'
import type { TreeViewBaseItem } from '@mui/x-tree-view/models'
import type { CSSProperties, FC, SyntheticEvent } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { startTransition } from 'react'
import { ThemeMode } from 'types/config'
import type { Period } from 'types/periods'
import { parsePeriodDate } from 'types/periods'

interface MultiPeriodSelectorProps {
  periods: Period[]
  selectedPeriods: string[]
  className?: string
  style?: CSSProperties
  width?: string | number
  handleChange: (selectedPeriods: string[]) => void
  label?: string
  rootPeriodOnly?: boolean
  sortOrder?: 'asc' | 'desc'
  timeFilter?: 'pastOnly' | 'futureOnly' | 'standard'
  disabled?: boolean
  disableMultiRootSelection?: boolean
  space?: number
  variant?: 'standard' | 'outlined'
  shrinkLabel?: boolean
  placeholder?: string
  labelColor?: string
  labelBackgroundColor?: string
  disableClosed?: boolean
  onlySingleRootPeriodSelection?: boolean
  autoSelectDescendants?: boolean
}

const MultiPeriodSelector: FC<MultiPeriodSelectorProps> = ({
  periods,
  selectedPeriods,
  className,
  style,
  width = '100%',
  handleChange,
  label = 'Select Periods',
  rootPeriodOnly = false,
  sortOrder = 'asc',
  timeFilter = 'standard',
  disabled = false,
  disableMultiRootSelection = false,
  variant = 'outlined',
  placeholder = 'Select Periods',
  labelColor = undefined,
  labelBackgroundColor = undefined,
  disableClosed = false,
  onlySingleRootPeriodSelection = false,
  autoSelectDescendants = true,
}) => {
  const theme = useTheme()
  const apiRef = useTreeViewApiRef()
  const toggledItemRef = useRef<{ [itemId: string]: boolean }>({})
  const anchorRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  // Helper function to find period by ID
  const findPeriodById = useCallback((periods: Period[], id: string): Period | undefined => {
    for (const period of periods) {
      if (period.id === id) {
        return period
      }
      if (period.children) {
        const found = findPeriodById(period.children, id)
        if (found) return found
      }
    }
    return undefined
  }, [])

  const handleItemSelectionToggle = useCallback((event: SyntheticEvent, itemId: string, isSelected: boolean) => {
    startTransition(() => {
      toggledItemRef.current[itemId] = isSelected
    })
  }, [])

  const getItemDescendantsIds = useCallback(
    (item: TreeViewBaseItem, periods: Period[]) => {
      const ids: string[] = []
      item.children?.forEach((child) => {
        // Check if the child is disabled due to being closed
        let shouldInclude = true
        if (disableClosed) {
          const period = findPeriodById(periods, child.id)
          if (period?.isClosed) {
            shouldInclude = false
          }
        }

        if (shouldInclude) {
          ids.push(child.id)
          ids.push(...getItemDescendantsIds(child, periods))
        }
      })
      return ids
    },
    [disableClosed, findPeriodById]
  )

  const handleSelectedItemsChange = useCallback(
    (event: SyntheticEvent, newSelectedItems: string[]) => {
      startTransition(() => {
        if (!autoSelectDescendants) {
          handleChange(newSelectedItems)
          toggledItemRef.current = {}
          return
        }

        const itemsToSelect: string[] = []
        const itemsToUnSelect: { [itemId: string]: boolean } = {}
        Object.entries(toggledItemRef.current).forEach(([itemId, isSelected]) => {
          const item = apiRef.current!.getItem(itemId)
          if (isSelected) {
            itemsToSelect.push(...getItemDescendantsIds(item, periods))
          } else {
            getItemDescendantsIds(item, periods).forEach((descendantId) => {
              itemsToUnSelect[descendantId] = true
            })
          }
        })

        const newSelectedItemsWithChildren = Array.from(
          new Set([...newSelectedItems, ...itemsToSelect].filter((itemId) => !itemsToUnSelect[itemId]))
        )

        handleChange(newSelectedItemsWithChildren)
        toggledItemRef.current = {}
      })
    },
    [autoSelectDescendants, handleChange, getItemDescendantsIds, periods]
  )

  const filteredPeriods = useMemo(() => {
    const now = new Date()
    const filterPeriod = (period: Period): Period | null => {
      if (disableClosed && period.isClosed && period.rootPeriodId !== null && period.rootPeriodId === period.id) {
        return null
      }

      const startDate = parsePeriodDate(period.startDate)
      const endDate = parsePeriodDate(period.endDate)

      if (!startDate || !endDate) {
        return period // Include periods with invalid dates
      }

      let result: boolean
      switch (timeFilter) {
        case 'pastOnly':
          result = startDate <= now
          break
        case 'futureOnly':
          result = startDate > now
          break
        default:
          result = true
      }

      if (!result) return null

      if (period.children && !rootPeriodOnly) {
        const filteredChildren = period.children.map(filterPeriod).filter((child): child is Period => child !== null)
        return { ...period, children: filteredChildren }
      }

      return period
    }

    return periods.map(filterPeriod).filter((period): period is Period => period !== null)
  }, [periods, timeFilter, rootPeriodOnly, disableClosed])

  const sortedPeriods = useMemo(() => {
    return [...filteredPeriods].sort((a, b) => {
      const dateA = new Date(a.endDate)
      const dateB = new Date(b.endDate)
      return sortOrder === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime()
    })
  }, [filteredPeriods, sortOrder])

  const renderTree = useCallback(
    (nodes: Period[] = []): TreeViewBaseItem[] =>
      nodes
        .filter((node) => node.id)
        .map((node) => ({
          id: node.id!,
          label: node.label,
          children: rootPeriodOnly ? [] : renderTree(node.children),
        })),
    [rootPeriodOnly]
  )

  const treeItems = useMemo(() => renderTree(sortedPeriods), [renderTree, sortedPeriods])

  const firstRootItemId = useMemo(() => {
    return treeItems[0]?.id
  }, [treeItems])

  const isItemDisabled = useCallback(
    (item: TreeViewBaseItem): boolean => {
      if (disableClosed) {
        const period = findPeriodById(sortedPeriods, item.id)
        if (period?.isClosed) {
          return true
        }
      }

      if (onlySingleRootPeriodSelection) {
        const currentPeriod = findPeriodById(sortedPeriods, item.id)
        if (!currentPeriod) return true

        if (selectedPeriods.length > 0) {
          const firstSelectedPeriod = findPeriodById(sortedPeriods, selectedPeriods[0])
          if (!firstSelectedPeriod) return true

          // If the rootPeriodIds don't match, disable the item
          if (currentPeriod.rootPeriodId !== firstSelectedPeriod.rootPeriodId) {
            return true
          }
        }
      }

      if (disableMultiRootSelection) {
        const rootAndDepth = findRootIdAndDepth(treeItems, item.id)

        if (selectedPeriods.length < 1) return false

        const selectedRootAndDepth = findRootIdAndDepth(treeItems, selectedPeriods[0])
        if (rootAndDepth?.rootId !== selectedRootAndDepth?.rootId) return true
      }

      return false
    },
    [disableClosed, sortedPeriods, onlySingleRootPeriodSelection, selectedPeriods, disableMultiRootSelection, treeItems]
  )

  const hoverColor = theme.palette.mode === ThemeMode.DARK ? theme.palette.action.hover : theme.palette.action.hover

  const findRootIdAndDepth = useCallback(
    (
      treeData: TreeViewBaseItem[],
      nodeId: string,
      depth: number = 0,
      rootId: string | null = null
    ): { rootId: string; depth: number } | null => {
      for (const treeNode of treeData) {
        if (!rootId || depth === 0) {
          rootId = treeNode.id
        }
        if (treeNode.id === nodeId) {
          return { rootId, depth }
        }
        if (treeNode.children) {
          const result = findRootIdAndDepth(treeNode.children, nodeId, depth + 1, rootId)
          if (result) {
            return result // Return the root item at the current level
          }
        }
      }
      return null // Return null if the item ID is not found in the tree
    },
    []
  )

  // Selected period names for display
  const selectedPeriodNames = useMemo(() => {
    return selectedPeriods.map((id) => {
      const period = findPeriodById(sortedPeriods, id)
      return period?.label || id
    })
  }, [selectedPeriods, sortedPeriods, findPeriodById])

  const handleInputClick = useCallback(() => {
    if (!disabled) {
      setOpen((prev) => !prev)
    }
  }, [disabled])

  const handleClickAway = useCallback(() => {
    setOpen(false)
  }, [])

  const renderValue = () => {
    if (selectedPeriodNames.length === 0) {
      return (
        <Box
          sx={{
            color: theme.palette.text.secondary,
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {placeholder}
        </Box>
      )
    }

    const displayText =
      selectedPeriodNames.length === 1
        ? selectedPeriodNames[0]
        : `${selectedPeriodNames.join(', ')} (${selectedPeriodNames.length} selected)`

    return (
      <Box
        sx={{
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: theme.palette.text.primary,
        }}
        title={selectedPeriodNames.join(', ')}
      >
        {displayText}
      </Box>
    )
  }

  return (
    <div className={className} style={{ ...style, width }}>
      <FormControl variant={variant} sx={{ width }}>
        <InputLabel
          shrink
          variant={variant}
          sx={{
            '&.MuiInputLabel-shrink': {
              background: labelBackgroundColor || 'inherit',
              color: labelColor || undefined,
              paddingLeft: '12px',
              paddingRight: '12px',
            },
          }}
        >
          {label}
        </InputLabel>

        <ClickAwayListener onClickAway={handleClickAway}>
          <div>
            <OutlinedInput
              ref={anchorRef}
              readOnly
              value=""
              onClick={handleInputClick}
              disabled={disabled}
              label={label}
              sx={{
                cursor: disabled ? 'default' : 'pointer',
                borderRadius: '21px',
                width,
                '& .MuiOutlinedInput-input': {
                  padding: '10.5px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                '& .MuiOutlinedInput-root': {
                  width,
                },
              }}
              inputProps={{
                'aria-expanded': open,
                'aria-haspopup': 'listbox',
                'aria-label': label,
              }}
              startAdornment={renderValue()}
            />

            <Popper
              open={open}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              sx={{ zIndex: theme.zIndex.modal }}
              modifiers={[
                {
                  name: 'flip',
                  enabled: true,
                },
                {
                  name: 'preventOverflow',
                  enabled: true,
                },
              ]}
            >
              <Paper
                sx={{
                  width: anchorRef.current?.offsetWidth || 'auto',
                  maxHeight: 300,
                  overflow: 'auto',
                  mt: 1,
                  boxShadow: theme.shadows[8],
                }}
              >
                <RichTreeView
                  multiSelect
                  checkboxSelection
                  apiRef={apiRef}
                  items={treeItems}
                  isItemDisabled={isItemDisabled}
                  selectedItems={selectedPeriods}
                  onSelectedItemsChange={handleSelectedItemsChange}
                  onItemSelectionToggle={handleItemSelectionToggle}
                  defaultExpandedItems={firstRootItemId ? [firstRootItemId] : []}
                  sx={{
                    '& .MuiTreeItem-content:hover': {
                      backgroundColor: hoverColor,
                    },
                    '& .MuiTreeItem-content.Mui-selected:hover': {
                      backgroundColor: theme.palette.action.selected,
                    },
                  }}
                />
              </Paper>
            </Popper>
          </div>
        </ClickAwayListener>
      </FormControl>
    </div>
  )
}

export default MultiPeriodSelector
